import { useState } from 'react';
import type { FeedItem } from '@/api/base-types';
import { api } from '@/api/client';
import { useM3Feed, useM3Mutation, type M3FeedScope } from '@/api/m3';
import { Button } from '@/components/kit/Button';
import { Feed } from '@/components/kit/Feed';
import { Skeleton } from '@/components/kit/Note';
import { Panel, PanelEmpty } from '@/components/kit/Panel';
import { useToast } from '@/components/kit/Toast';
import { errorText } from '@/features/base/shared/useUndoable';

/**
 * Лента заявки, сделки или номера (feedPanel в макете): композер
 * «Комментарий / Звонок» подчёркиванием, ниже записи по дням с пометкой
 * контекста. Комментарий из сделки виден и в карточке клиента (БЛ M3 3.8).
 */
export function M3Feed({ scope, id }: { scope: M3FeedScope; id: string }) {
  const toast = useToast();
  const feed = useM3Feed(scope, id);
  const [kind, setKind] = useState<'comment' | 'call'>('comment');
  const [text, setText] = useState('');
  const add = useM3Mutation((body: { kind: 'comment' | 'call'; text: string }) =>
    api.post<FeedItem>(`/${scope}/${id}/feed`, body),
  );
  const items = feed.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Panel title="Лента">
      <div className="cmp">
        <div className="ctabs">
          <span className={kind === 'comment' ? 'on' : ''} onClick={() => setKind('comment')}>
            Комментарий
          </span>
          <span className={kind === 'call' ? 'on' : ''} onClick={() => setKind('call')}>
            Звонок
          </span>
        </div>
        <label className="field">
          <textarea
            value={text}
            aria-label={kind === 'call' ? 'Звонок' : 'Комментарий'}
            placeholder={kind === 'call' ? 'О чём договорились по телефону' : 'Комментарий виден и в карточке клиента'}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <Button
          small
          disabled={!text.trim() || add.isPending}
          onClick={() =>
            add.mutate(
              { kind, text },
              {
                onSuccess: () => {
                  setText('');
                  toast.show(kind === 'call' ? 'Звонок записан' : 'Комментарий добавлен');
                },
                onError: (e) => toast.showError(errorText(e)),
              },
            )
          }
        >
          {kind === 'call' ? 'Записать звонок' : 'Добавить комментарий'}
        </Button>
      </div>
      <hr className="hair" />
      {feed.isLoading ? (
        <div className="fpad">
          <Skeleton width="40%" />
        </div>
      ) : feed.isError ? (
        <div className="fpad ph-empty">Ленту не удалось загрузить.</div>
      ) : items.length ? (
        <Feed items={items} />
      ) : (
        <div className="fpad">
          <PanelEmpty>Событий нет.</PanelEmpty>
        </div>
      )}
      {feed.hasNextPage ? (
        <Button small tone="ghost" onClick={() => void feed.fetchNextPage()} disabled={feed.isFetchingNextPage}>
          Показать ещё
        </Button>
      ) : null}
    </Panel>
  );
}

/**
 * Блок «Задачи» — осознанное исключение из правила «заглушек нет»
 * (ТЗ M3 §1, БЛ 11.6, CLAUDE.md): задачи появятся своим модулем.
 */
export function TasksStub({ of }: { of: string }) {
  return (
    <Panel title="Задачи">
      <PanelEmpty>Здесь будут задачи по {of} — появятся вместе с модулем «Задачи».</PanelEmpty>
    </Panel>
  );
}
